import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export const fetchCart = createAsyncThunk('cart/fetch', async (_, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem('access_token');
    const res = await axios.get(`${API_URL}/orders/cart/`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    // Map server items to UI shape
    const items = (res.data.items || []).map(ci => ({
      id: ci.product?.id || ci.product_id,
      name: ci.product?.name,
      price: ci.product?.final_price ?? ci.product?.price,
      image: ci.product?.main_image,
      quantity: ci.quantity,
    }));
    return items;
  } catch (e) {
    return rejectWithValue(e.response?.data || 'Failed to fetch cart');
  }
});

export const addToServerCart = createAsyncThunk('cart/add', async ({ productId, quantity = 1 }, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem('access_token');
    await axios.post(`${API_URL}/orders/cart/add/`, { product_id: productId, quantity }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return { productId, quantity };
  } catch (e) {
    return rejectWithValue(e.response?.data || 'Failed to add to cart');
  }
});

export const updateServerCartItem = createAsyncThunk('cart/update', async ({ cartItemId, productId, quantity }, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem('access_token');
    await axios.patch(`${API_URL}/orders/cart/update/${cartItemId || productId}/`, { quantity }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return { productId, quantity };
  } catch (e) {
    return rejectWithValue(e.response?.data || 'Failed to update quantity');
  }
});

export const removeFromServerCart = createAsyncThunk('cart/remove', async ({ cartItemId, productId }, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem('access_token');
    await axios.delete(`${API_URL}/orders/cart/remove/${cartItemId || productId}/`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return productId;
  } catch (e) {
    return rejectWithValue(e.response?.data || 'Failed to remove item');
  }
});

export const clearServerCart = createAsyncThunk('cart/clear', async (_, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem('access_token');
    await axios.delete(`${API_URL}/orders/cart/clear/`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return true;
  } catch (e) {
    return rejectWithValue(e.response?.data || 'Failed to clear cart');
  }
});

const cartSlice = createSlice({
  name: 'cart',
  initialState: {
    items: [],
    loading: false,
    error: null,
  },
  reducers: {
    setCartItems: (state, action) => {
      state.items = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCart.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchCart.fulfilled, (state, action) => { state.loading = false; state.items = action.payload; })
      .addCase(fetchCart.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(addToServerCart.fulfilled, (state, action) => {
        const { productId, quantity } = action.payload;
        const item = state.items.find(i => i.id === productId);
        if (item) item.quantity += quantity; else state.items.push({ id: productId, quantity });
      })
      .addCase(updateServerCartItem.fulfilled, (state, action) => {
        const { productId, quantity } = action.payload;
        const item = state.items.find(i => i.id === productId);
        if (item) item.quantity = quantity;
      })
      .addCase(removeFromServerCart.fulfilled, (state, action) => {
        const productId = action.payload;
        state.items = state.items.filter(i => i.id !== productId);
      })
      .addCase(clearServerCart.fulfilled, (state) => { state.items = []; });
  }
});

export const {
  setCartItems,
} = cartSlice.actions;

export default cartSlice.reducer;
